/**
 * SecurityGroups — create permission packs and assign members.
 * Actor: system_admin, business_admin
 * Route: /admin/security-groups
 */
import { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  AppBadge, AppButton, AppCard, AppField, AppInput, AppModal, AppSelect,
} from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { IconCheck, IconSpinner } from "../../../shared/components/icons";
import { api } from "../../../shared/api/client";
import { getUser } from "../../auth/session";

type SecurityGroup = {
  id: number;
  code: string;
  name: string;
  description: string | null;
  permissions: string[];
  is_system: boolean;
  is_active: boolean;
  member_count?: number;
};

type Member = {
  user_id: number;
  username: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
};

type UserRow = { id: number; username: string; role: string; is_active: boolean };
type TenantOpt = { id: number; name: string; code: string };

export function SecurityGroups() {
  const caller = getUser();
  const isPlatform = caller?.role === "system_admin" || caller?.account_scope === "platform";
  const [groups, setGroups] = useState<SecurityGroup[]>([]);
  const [catalogue, setCatalogue] = useState<string[]>([]);
  const [tenants, setTenants] = useState<TenantOpt[]>([]);
  const [platformTenantId, setPlatformTenantId] = useState("");
  const [pageState, setPageState] = useState<"loading" | "error" | "success">("loading");
  const [pageError, setPageError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<SecurityGroup | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [addUserId, setAddUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState("");
  const [form, setForm] = useState({
    code: "",
    name: "",
    description: "",
    role_ceiling: "sales_rep",
    permissions: [] as string[],
    tenant_id: "",
  });

  const load = async () => {
    setPageState("loading");
    try {
      if (isPlatform) {
        const td = await api.get<{ tenants: TenantOpt[] }>("/tenants");
        setTenants(td.tenants ?? []);
      }
      const seedBody = isPlatform && platformTenantId
        ? { tenant_id: Number(platformTenantId) }
        : {};
      if (!isPlatform || platformTenantId) {
        await api.post("/security-groups/seed", seedBody).catch(() => undefined);
      }
      const [gd, cd] = await Promise.all([
        api.get<{ security_groups: SecurityGroup[] }>(
          isPlatform && platformTenantId
            ? `/security-groups?tenant_id=${encodeURIComponent(platformTenantId)}`
            : "/security-groups",
        ),
        api.get<{ permissions: string[] }>("/permissions/catalogue"),
      ]);
      setGroups(gd.security_groups ?? []);
      setCatalogue(cd.permissions ?? []);
      setPageState("success");
    } catch (ex: unknown) {
      setPageError(ex instanceof Error ? ex.message : "Failed to load security groups");
      setPageState("error");
    }
  };

  useEffect(() => { void load(); }, [isPlatform, platformTenantId]);

  const loadCeiling = async (role: string) => {
    try {
      const d = await api.get<{ permissions: string[] }>(
        `/permissions/catalogue?role=${encodeURIComponent(role)}`,
      );
      setCatalogue(d.permissions ?? []);
    } catch { /* keep previous */ }
  };

  const openCreate = () => {
    setForm({
      code: "",
      name: "",
      description: "",
      role_ceiling: "sales_rep",
      permissions: [],
      tenant_id: platformTenantId,
    });
    setFormErr("");
    loadCeiling("sales_rep");
    setCreateOpen(true);
  };

  const openEdit = async (g: SecurityGroup) => {
    setEditGroup(g);
    setForm({
      code: g.code,
      name: g.name,
      description: g.description || "",
      role_ceiling: "",
      permissions: [...(g.permissions || [])],
      tenant_id: platformTenantId,
    });
    setFormErr("");
    try {
      const d = await api.get<{ permissions: string[] }>("/permissions/catalogue");
      setCatalogue(d.permissions ?? []);
    } catch { /* */ }
  };

  const openDetail = async (id: number) => {
    setDetailId(id);
    try {
      const [md, ud] = await Promise.all([
        api.get<{ members: Member[] }>(`/security-groups/${id}`),
        api.get<{ users: UserRow[] }>("/users?per_page=200"),
      ]);
      setMembers(md.members ?? []);
      setUsers((ud.users ?? []).filter((u) => u.is_active));
      setAddUserId("");
    } catch {
      setMembers([]);
    }
  };

  const togglePerm = (p: string) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(p)
        ? f.permissions.filter((x) => x !== p)
        : [...f.permissions, p],
    }));
  };

  const submitCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code || !form.name) {
      setFormErr("Code and name are required");
      return;
    }
    if (isPlatform && !form.tenant_id) {
      setFormErr("Select a company (tenant) for this security group");
      return;
    }
    setBusy(true); setFormErr("");
    try {
      const body: Record<string, unknown> = {
        code: form.code,
        name: form.name,
        description: form.description || undefined,
        permissions: form.permissions,
        role_ceiling: form.role_ceiling || undefined,
      };
      if (isPlatform) body.tenant_id = Number(form.tenant_id);
      await api.post("/security-groups", body);
      setCreateOpen(false);
      await load();
    } catch (ex: unknown) {
      setFormErr(ex instanceof Error ? ex.message : "Failed");
    } finally { setBusy(false); }
  };

  const submitEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editGroup) return;
    setBusy(true); setFormErr("");
    try {
      await api.put(`/security-groups/${editGroup.id}`, {
        name: form.name,
        description: form.description,
        permissions: form.permissions,
      });
      setEditGroup(null);
      await load();
    } catch (ex: unknown) {
      setFormErr(ex instanceof Error ? ex.message : "Failed");
    } finally { setBusy(false); }
  };

  const addMember = async () => {
    if (!detailId || !addUserId) return;
    setBusy(true);
    try {
      await api.post(`/security-groups/${detailId}/members`, { user_id: parseInt(addUserId, 10) });
      await openDetail(detailId);
      await load();
    } finally { setBusy(false); }
  };

  const removeMember = async (userId: number) => {
    if (!detailId) return;
    setBusy(true);
    try {
      await api.delete(`/security-groups/${detailId}/members/${userId}`);
      await openDetail(detailId);
      await load();
    } finally { setBusy(false); }
  };

  const columns: ColumnDef<SecurityGroup, unknown>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "code", header: "Code" },
    {
      id: "type",
      header: "Type",
      cell: ({ row }) =>
        row.original.is_system
          ? <AppBadge tone="info">System</AppBadge>
          : <AppBadge tone="neutral">Custom</AppBadge>,
    },
    {
      id: "perms",
      header: "Permissions",
      accessorFn: (r) => `${(r.permissions || []).length}`,
    },
    {
      id: "members",
      header: "Members",
      accessorFn: (r) => String(r.member_count ?? 0),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) =>
        row.original.is_active
          ? <AppBadge tone="success"><IconCheck size={12} /> Active</AppBadge>
          : <AppBadge tone="neutral">Inactive</AppBadge>,
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => (
        <div className="pg-row-actions">
          <AppButton type="button" variant="secondary" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}>
            Edit
          </AppButton>
          <AppButton type="button" variant="secondary" onClick={(e) => { e.stopPropagation(); openDetail(row.original.id); }}>
            Members
          </AppButton>
        </div>
      ),
    },
  ];

  const permPicker = (
    <div className="pg-perm-grid" style={{ maxHeight: 220, overflow: "auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
      {catalogue.map((p) => (
        <label key={p} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
          <input
            type="checkbox"
            checked={form.permissions.includes(p)}
            onChange={() => togglePerm(p)}
            disabled={busy}
          />
          {p}
        </label>
      ))}
    </div>
  );

  const listStatus =
    pageState === "success" && groups.length === 0 ? "empty" : pageState;

  return (
    <>
      {createOpen && (
        <AppModal open={createOpen} onClose={() => setCreateOpen(false)} title="Create Security Group">
          <form onSubmit={submitCreate}>
            {isPlatform && (
              <AppField label="Company">
                <AppSelect
                  value={form.tenant_id}
                  onChange={(e) => setForm((f) => ({ ...f, tenant_id: e.target.value }))}
                  disabled={busy}
                >
                  <option value="">Select company…</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>{t.name} ({t.code})</option>
                  ))}
                </AppSelect>
              </AppField>
            )}
            <AppField label="Code (e.g. sales_1)">
              <AppInput value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toLowerCase() }))} disabled={busy} autoFocus />
            </AppField>
            <AppField label="Display name (e.g. Sales 1)">
              <AppInput value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} disabled={busy} />
            </AppField>
            <AppField label="Description">
              <AppInput value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} disabled={busy} />
            </AppField>
            <AppField label="Permission ceiling (role template)">
              <AppSelect
                value={form.role_ceiling}
                onChange={(e) => {
                  const role = e.target.value;
                  setForm((f) => ({ ...f, role_ceiling: role, permissions: [] }));
                  loadCeiling(role);
                }}
                disabled={busy}
              >
                <option value="sales_rep">Sales Rep</option>
                <option value="technician">Technician</option>
                <option value="commercial_officer">Commercial Officer</option>
                <option value="business_admin">Business Admin</option>
                <option value="village_chairman">Village Chairman</option>
                <option value="m_and_e_officer">M&E Officer</option>
                <option value="rea_officer">REA Officer</option>
              </AppSelect>
            </AppField>
            <AppField label="Permissions" error={formErr}>{permPicker}</AppField>
            <div className="pg-modal-actions">
              <AppButton type="submit" disabled={busy}>{busy ? <><IconSpinner size={14} /> Saving…</> : "Create"}</AppButton>
              <AppButton type="button" variant="secondary" onClick={() => setCreateOpen(false)} disabled={busy}>Cancel</AppButton>
            </div>
          </form>
        </AppModal>
      )}

      {editGroup && (
        <AppModal open={!!editGroup} onClose={() => setEditGroup(null)} title={`Edit: ${editGroup.name}`}>
          <form onSubmit={submitEdit}>
            <AppField label="Code"><AppInput value={form.code} disabled /></AppField>
            <AppField label="Display name">
              <AppInput value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} disabled={busy} />
            </AppField>
            <AppField label="Description">
              <AppInput value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} disabled={busy} />
            </AppField>
            <AppField label="Permissions" error={formErr}>{permPicker}</AppField>
            <div className="pg-modal-actions">
              <AppButton type="submit" disabled={busy}>{busy ? <><IconSpinner size={14} /> Saving…</> : "Save"}</AppButton>
              <AppButton type="button" variant="secondary" onClick={() => setEditGroup(null)} disabled={busy}>Cancel</AppButton>
            </div>
          </form>
        </AppModal>
      )}

      {detailId != null && (
        <AppModal open={detailId != null} onClose={() => setDetailId(null)} title="Group members">
          <AppField label="Add user">
            <div style={{ display: "flex", gap: 8 }}>
              <AppSelect value={addUserId} onChange={(e) => setAddUserId(e.target.value)} disabled={busy}>
                <option value="">Select user…</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
                ))}
              </AppSelect>
              <AppButton type="button" onClick={addMember} disabled={busy || !addUserId}>Add</AppButton>
            </div>
          </AppField>
          {members.length === 0 ? (
            <p className="pg-muted">No members yet.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0 }}>
              {members.map((m) => (
                <li key={m.user_id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--pg-border, #e5e7eb)" }}>
                  <span>{m.username} — {m.role}</span>
                  <AppButton type="button" variant="secondary" onClick={() => removeMember(m.user_id)} disabled={busy}>Remove</AppButton>
                </li>
              ))}
            </ul>
          )}
        </AppModal>
      )}

      <PageHeader
        title="Security Groups"
        subtitle="Permission packs assigned within a user's base role."
        actions={
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {isPlatform && (
              <AppSelect
                value={platformTenantId}
                onChange={(e) => setPlatformTenantId(e.target.value)}
                aria-label="Company filter"
              >
                <option value="">Select company…</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.code})</option>
                ))}
              </AppSelect>
            )}
            <AppButton
              type="button"
              onClick={openCreate}
              disabled={isPlatform && !platformTenantId && !form.tenant_id && tenants.length === 0}
            >
              Create Group
            </AppButton>
          </div>
        }
      />

      {isPlatform && !platformTenantId ? (
        <AppCard>
          <p className="pg-muted">Select a company to view or seed that company&apos;s security groups.</p>
        </AppCard>
      ) : (
      <PageState
        status={listStatus}
        error={pageError}
        onRetry={load}
        emptyTitle="No security groups"
        emptyHint="Create a group to bundle permissions for your team."
      >
        <AppCard>
          <AppDataTable data={groups} columns={columns} />
        </AppCard>
      </PageState>
      )}
    </>
  );
}
